// The data-type coercion matrix for driven fields, as the WEB sees it: given a
// SOURCE element type and a TARGET element type, what may flow between them,
// and through which transform? Nothing here writes data — this module exists
// so the driven-from modal can sort, grey out, and warn about element pairs
// without a round trip per keystroke.
//
// Three classes:
//   exact      — same shape, no transform needed ('none' only).
//   coercible  — legal through one of the offered transforms; `lossy`
//                transforms carry a warning the modal must surface.
//   unmappable — refused. Unknown/typo'd dataTypes are unmappable too: this
//                module FAILS CLOSED, unlike the platform's usual
//                fall-through-to-text rendering.
//
// MIRROR of the canonical module in keepr-api at utils/automations/coercion.js,
// which is the one that VALIDATES and applies these rules on write. The two
// must agree — each side's tests pin the same classifications, so a drift shows
// up as a red suite rather than a modal offering a transform the API rejects.
// Change them together. `applyTransform` is deliberately NOT mirrored: the web
// never transforms a value, the engine does.

import { allowedMeasures, getMeasure, isMultiMeasure, isOwnUnitId, ownUnitOf, sameOwnUnit } from '../utils/units';
import { allowedCodes } from '../utils/currency';

// All 24 known dataTypes: the 22 in src/utils/dictionary.js plus the legacy
// numeric spellings still honored by option policy / formatters. A new type is
// APPENDED, never inserted, so every index-keyed pin stays put: `currency` —
// money in one of an element's ISO 4217 currencies (utils/currency/) — and
// then `color` — one "#rrggbb" (utils/elementFormat/color.js;
// docs/plans/data-types-second-pass.md §4.1) — and then `file`, an
// attachment id or a list of them (docs/plans/data-types-file-element.md §2)
// — and then `confirmations`, how many people said "me too" (KPR-390; keepr-api
// utils/confirmations.js) — and then `repeating-group`, an ordered list of
// entries each holding the group's own columns (KPR-679; keepr-api
// utils/repeatingGroups.js, KPR-675).
export const KNOWN_TYPES = [
	'text-small', 'text-large', 'rich-text', 'choice', 'number', 'decimal',
	'integer', 'boolean', 'date', 'date-time', 'time', 'url', 'phone', 'email',
	'location', 'rating', 'card-lookup', 'measurement', 'user', 'currency',
	'color', 'file', 'confirmations', 'repeating-group',
];

const NUMERIC = new Set(['number', 'decimal', 'integer', 'rating']);
const MEASUREMENT = 'measurement';
const CURRENCY = 'currency';
const BOOLEAN = 'boolean';
const PLAIN_TEXT = new Set(['text-small', 'text-large', 'url', 'choice']);
const DATEISH = new Set(['date', 'date-time']);
// The three TEXT-ADJACENT types (docs/plans/data-types.md decision 1). They
// are deliberately NOT members of PLAIN_TEXT: their stored values are strings
// (an E.164 number, an address) or an object (a location), so they READ as
// text everywhere, but each has a write-time rule of its own, and a matrix
// that let any text flow into any of them would promise conversions the item
// write path then refuses with a 400. classifyContact() below is the row.
const CONTACT = new Set(['phone', 'email']);
const LOCATION = 'location';
const TEXT_TARGETS = new Set(['text-small', 'text-large', 'rich-text']);
// A `user` value is an account id (keepr-api utils/userElements.js). It flows
// into PLAIN text as the account's display name — lossy, since a name is not
// a reference — and nothing flows INTO a user element but another user
// element: an account is chosen, never computed.
const USER = 'user';
const USER_TEXT_TARGETS = new Set(['text-small', 'text-large']);
// A `color` value is one lower-case "#rrggbb" string: it reads as text
// exactly, and text reads back into it only where the text spells a color.
const COLOR = 'color';
const COLOR_TEXT_SOURCES = new Set(['text-small', 'text-large', 'rich-text', 'choice']);
// A `file` value is an attachment id bound to its OWN item (one attachment,
// one place — the 10b contract), so nothing flows into or out of it: a copy
// of the id into another element (or another item) would be a second place
// for the same file, and no text, number or date names a file. The card
// editor never offers `drivenFrom` on one either (elementOptionPolicy).
const FILE = 'file';
// A `confirmations` value is a COUNT keepr keeps from its ledger of who said
// "me too" (KPR-390). Nothing flows into one — a count a rule wrote would name
// people nobody asked — and it flows into nothing either: a driven copy is
// refreshed when its source item is SAVED, and a confirmation is not a save,
// so the copy would go stale on the next "me too" and stay wrong. Not a
// NUMERIC member for the same reason, though it reads as a number everywhere.
const CONFIRMATIONS = 'confirmations';
// A `repeating-group` value is an ordered list of entries, each an object of
// the group's own columns (keepr-api utils/repeatingGroups.js, KPR-675).
// Nothing flows into one — its entries are written by people — and it flows
// into nothing: one element cannot hold a list of entries. Group to group is
// refused too (the API's matrix refuses it before the same-type `exact`).
const REPEATING_GROUP = 'repeating-group';

// Transform metadata only — the lossy flag decides whether the modal shows a
// warning, and the warning string is the one the user reads. Wording is copied
// verbatim from the API module so both surfaces say the same thing.
const TRANSFORMS = {
	none: { lossy: false },
	toText: { lossy: false },
	truncate: {
		lossy: true,
		warning: 'Values longer than 255 characters are cut off.',
	},
	stripFormatting: {
		lossy: true,
		warning: 'Rich-text formatting (bold, headings, links, lists) is removed; only the plain text is kept.',
	},
	dateToText: { lossy: false },
	dateTimeToDate: {
		lossy: true,
		warning: 'The time of day is dropped; only the date is kept.',
	},
	dateToDateTime: {
		lossy: true,
		warning: 'A time of day of midnight (UTC) is assumed.',
	},
	// Split by target shape (as in the API module): a `date` element holds
	// YYYY-MM-DD, a `date-time` element the full ISO timestamp.
	numberToDate: {
		lossy: true,
		warning: 'The number is interpreted as milliseconds since 1970 (epoch time).',
	},
	numberToDateTime: {
		lossy: true,
		warning: 'The number is interpreted as milliseconds since 1970 (epoch time).',
	},
	dateToNumber: {
		lossy: true,
		warning: 'The date becomes milliseconds since 1970 (epoch time).',
	},
	// Measurement values are { value, unit, base } objects. Crossing into a
	// bare number drops the unit: the number is the magnitude in the SOURCE
	// element's default unit, so the reader of the target still knows what it
	// means from the source's definition.
	measurementToNumber: {
		lossy: true,
		warning: 'The unit is dropped; the number is the value in the source element\'s default unit.',
	},
	// A number into a measurement element is read in the TARGET's default
	// unit (a bare number means the default unit everywhere on the platform).
	numberToMeasurement: { lossy: false },
	// Currency values are { amount, currency } objects, amount in MINOR units.
	// Crossing into a bare number drops the code and yields MAJOR units --
	// 12.5, never 1250 -- because that is the number a person reads on the
	// source; which currency it was is lost.
	currencyToNumber: {
		lossy: true,
		warning: 'The currency is dropped; the number is the amount in major units (12.50, not 1250).',
	},
	// A number into a currency element is MAJOR units in the TARGET's default
	// currency, rounded half away from zero to the currency's decimals.
	numberToCurrency: {
		lossy: true,
		warning: 'Numbers are read in the element\'s default currency and rounded to its decimal places.',
	},
	// A yes/no crossing into text or a number keeps everything it knows: the
	// two states have exactly two spellings on the other side.
	booleanToText: { lossy: false },
	booleanToNumber: { lossy: false },
	textToBoolean: {
		lossy: true,
		warning: 'Only "yes", "no", "true" and "false" (and 1 / 0) convert; any other text leaves the value unset.',
	},
	numberToBoolean: {
		lossy: true,
		warning: 'Zero becomes No and every other number becomes Yes; the number itself is not kept.',
	},
	textToColor: {
		lossy: true,
		warning: 'Only a valid color converts; any other text leaves it blank.',
	},
	// A location is the one element value that is an OBJECT rather than a
	// scalar, so crossing into or out of text is a real conversion. Out: the
	// address if there is one, else the coordinates — what the element SHOWS.
	// In: the string becomes an address, unless it spells a coordinate pair.
	locationToText: {
		lossy: true,
		warning: 'Only what the location shows is copied — the coordinates are dropped when there is also an address.',
	},
	textToLocation: {
		lossy: true,
		warning: 'The text is stored as the address; only a "lat, lng" pair becomes coordinates.',
	},
	// A phone renders the way the READER sees it — national at home,
	// international abroad — because a text copy is for a human, and E.164 is
	// the storage spelling, not a legible one.
	phoneToText: { lossy: false },
	// An account reads as the person's display name; the link is gone.
	userToText: {
		lossy: true,
		warning: 'Only the display name is copied; the link to the account is lost.',
	},
	// "Allow multiple" on a choice (docs/plans/data-types-second-pass.md §3.5):
	// a list into one choice keeps its first entry (the list is stored in the
	// choices' order), one into a list is wrapped, a list into text is joined
	// with ", ". Mirrors keepr-api utils/automations/coercion.js.
	firstChoice: {
		lossy: true,
		warning: 'Only the first of the selected choices is kept.',
	},
	wrapChoice: { lossy: false },
	joinChoices: {
		lossy: true,
		warning: 'The selected choices are joined into one text, separated by commas.',
	},
};

const exact = () => ({ class: 'exact', transforms: ['none'] });
const coercible = (...transforms) => ({ class: 'coercible', transforms });
const unmappable = reason => ({ class: 'unmappable', reason });

/**
 * classify(sourceType, targetType, { sourceOptions, targetOptions }) ->
 *   { class, transforms?, reason? }
 * `transforms` lists the legal transform NAMES for the pair (details via
 * transformInfo). For the `count` aggregate, classify with sourceType
 * 'number' — the aggregate's output type, not the element's. The element
 * options are only consulted for measurement pairs: two measurement
 * elements are `exact` only when both name the same known measure (a mass
 * can never flow into a length).
 */
export function classify(sourceType, targetType, { sourceOptions, targetOptions } = {}) {
	if (!KNOWN_TYPES.includes(sourceType)) return unmappable(`Unknown data type "${sourceType}".`);
	if (!KNOWN_TYPES.includes(targetType)) return unmappable(`Unknown data type "${targetType}".`);

	// choice as a TARGET is closed: its values come from a fixed slug set no
	// transform can promise to land in. (As a SOURCE it behaves like text —
	// the stored value is a plain slug string.)
	if (targetType === 'choice' && sourceType !== 'choice') {
		return unmappable('Choice values are limited to the element\'s configured options.');
	}
	// "Allow multiple": a LIST of choices on either side decides the row
	// before anything reads the types alone (the API's matrix, row for row).
	const sourceMulti = sourceType === 'choice' && (sourceOptions || {}).allowMultiple === true;
	const targetMulti = targetType === 'choice' && (targetOptions || {}).allowMultiple === true;
	if (sourceType === 'choice' && targetType === 'choice') {
		if (sourceMulti === targetMulti) return exact();
		return sourceMulti ? coercible('firstChoice') : coercible('wrapChoice');
	}
	if (sourceMulti) {
		if (TEXT_TARGETS.has(targetType)) return coercible('joinChoices');
		return unmappable(`A choice that allows multiple values has no reading as ${targetType}; copy it into a text element instead.`);
	}
	if (sourceType === MEASUREMENT || targetType === MEASUREMENT) return classifyMeasurement(sourceType, targetType, sourceOptions, targetOptions);
	if (sourceType === CURRENCY || targetType === CURRENCY) return classifyCurrency(sourceType, targetType, sourceOptions, targetOptions);
	if (sourceType === USER || targetType === USER) return classifyUser(sourceType, targetType);
	if (sourceType === FILE || targetType === FILE) {
		return unmappable('A file element holds files attached to its own item; nothing converts into or out of it.');
	}
	// Straight after the file row, as in the API (worded as the API's). Not
	// hoisted above the measurement / currency / user rows: those settle a
	// pair first in this module, and their pinned reasons would move; every
	// row refuses a count, so only the sentence differs.
	if (sourceType === CONFIRMATIONS || targetType === CONFIRMATIONS) {
		return unmappable(targetType === CONFIRMATIONS
			? 'A count of confirmations is kept by keepr from the people who confirmed; nothing else can set it.'
			: 'A count of confirmations changes without its item being saved, so a copy of it would go stale.');
	}
	// Worded as the API's. Before the same-type `exact` below: even a group
	// into a group is refused.
	if (sourceType === REPEATING_GROUP || targetType === REPEATING_GROUP) {
		return unmappable(targetType === REPEATING_GROUP
			? 'A repeating group\'s entries are written by people; an automation cannot copy a value into one.'
			: 'A repeating group holds a list of entries; an automation cannot copy it into one element.');
	}
	if (sourceType === targetType) return exact();

	// card-lookup pairs only with card-lookup (same referenced card — checked
	// separately at rule validation, where both elements' configs are known).
	// A lookup value is an item id: meaningless as text/number/date.
	if (sourceType === 'card-lookup' || targetType === 'card-lookup') {
		return unmappable('Card lookups can only drive card lookups referencing the same card.');
	}

	// A yes/no is two states, so it reads as text ("Yes"/"No") or as 1/0, and
	// text or a number reads back into it. Nothing else: a date, a quantity or
	// a linked record is not a state. Mirrors the API's coercion module —
	// including that a URL is NOT a text target here: a link to "Yes" is not
	// a link, and the API refuses the pair.
	if (sourceType === BOOLEAN || targetType === BOOLEAN) {
		if (sourceType === BOOLEAN) {
			if (TEXT_TARGETS.has(targetType)) return coercible('booleanToText');
			if (NUMERIC.has(targetType)) return coercible('booleanToNumber');
			return unmappable(`No conversion from yes/no to ${targetType}.`);
		}
		if (PLAIN_TEXT.has(sourceType) || sourceType === 'rich-text') return coercible('textToBoolean');
		if (NUMERIC.has(sourceType)) return coercible('numberToBoolean');
		return unmappable(`No conversion from ${sourceType} to yes/no.`);
	}
	if (sourceType === COLOR || targetType === COLOR) return classifyColor(sourceType, targetType);

	if (CONTACT.has(sourceType) || CONTACT.has(targetType) || sourceType === LOCATION || targetType === LOCATION) {
		return classifyContact(sourceType, targetType);
	}

	if (NUMERIC.has(sourceType) && NUMERIC.has(targetType)) {
		// rating is numeric storage with a bounded display; out-of-scale
		// values render oddly but lose nothing.
		return targetType === 'rating'
			? coercible('none')
			: exact();
	}
	if (DATEISH.has(sourceType) && DATEISH.has(targetType)) {
		return sourceType === 'date-time'
			? coercible('dateTimeToDate')
			: coercible('dateToDateTime');
	}

	const sourceTextLike = PLAIN_TEXT.has(sourceType) || sourceType === 'rich-text';
	if (sourceTextLike && (PLAIN_TEXT.has(targetType) || targetType === 'rich-text')) {
		if (targetType === 'url') {
			// The text passes through untransformed, then meets the URL rule in
			// the API's driven gate (drivenFields.js gateDrivenValue): a value
			// the rule refuses is stored blank (security review D1, 2026-10-01).
			return coercible('none');
		}
		if (sourceType === 'rich-text') {
			return targetType === 'text-small'
				? coercible('stripFormatting', 'truncate')
				: coercible('stripFormatting');
		}
		if (sourceType === 'text-large' && targetType === 'text-small') return coercible('truncate');
		return exact(); // small->large, choice/url->text, plain text -> rich text (valid markdown)
	}

	if (NUMERIC.has(sourceType) && (PLAIN_TEXT.has(targetType) || targetType === 'rich-text')) {
		return coercible('toText');
	}
	if (DATEISH.has(sourceType) && (PLAIN_TEXT.has(targetType) || targetType === 'rich-text')) {
		return coercible('dateToText');
	}
	if (sourceType === 'time' && (PLAIN_TEXT.has(targetType) || targetType === 'rich-text')) {
		return coercible('toText');
	}
	if (NUMERIC.has(sourceType) && DATEISH.has(targetType)) {
		return coercible(targetType === 'date' ? 'numberToDate' : 'numberToDateTime');
	}
	if (DATEISH.has(sourceType) && NUMERIC.has(targetType)) return coercible('dateToNumber');

	return unmappable(`No conversion from ${sourceType} to ${targetType}.`);
}

/**
 * The phone / email / location rows of the matrix. Same-type pairs, choice
 * targets, measurements, booleans and card lookups were all settled above, so
 * every pair reaching here crosses between one of these three and something
 * else. MIRROR of the API's classifyContact — change them together.
 *
 * The shape of the rule: all three are TEXT-ADJACENT, so text flows both ways
 * — but only from types that are actually text. What is deliberately
 * UNMAPPABLE, and why:
 *
 *  - phone <-> email. Both are strings and both are "a way to reach someone",
 *    which is exactly why an automation copying one into the other would look
 *    plausible and be wrong every time.
 *  - phone / email <-> location. A telephone number is not a place.
 *  - number / date / time / rating <-> any of the three. A bare number is not
 *    a phone number (it has lost its country code and its leading zero), an
 *    address is not a quantity, and no date is either. Copy through a text
 *    element if a lossy spelling really is what is wanted — that detour is
 *    the point: it is visible in the rule.
 */
function classifyContact(sourceType, targetType) {
	const textSource = PLAIN_TEXT.has(sourceType) || sourceType === 'rich-text';

	if (sourceType === LOCATION) {
		if (TEXT_TARGETS.has(targetType) || targetType === 'url') return coercible('locationToText');
		return unmappable(`No conversion from a location to ${targetType}.`);
	}
	if (targetType === LOCATION) {
		if (textSource) return coercible('textToLocation');
		return unmappable(`No conversion from ${sourceType} to a location.`);
	}

	// phone / email as the SOURCE (the target is not a location, and the two
	// are not the same type — that was `exact` above).
	if (CONTACT.has(sourceType)) {
		if (CONTACT.has(targetType)) {
			return unmappable(sourceType === 'phone'
				? 'A phone number is not an email address.'
				: 'An email address is not a phone number.');
		}
		if (sourceType === 'phone' && (TEXT_TARGETS.has(targetType) || targetType === 'url')) return coercible('phoneToText');
		// An address into a url passes through untransformed, exactly as the
		// text rows treat every url target; the API's driven gate then holds it
		// to the URL rule.
		if (targetType === 'url') return coercible('none');
		if (TEXT_TARGETS.has(targetType)) return exact();
		return unmappable(`No conversion from ${sourceType} to ${targetType}.`);
	}

	// phone / email as the TARGET. A text source lands exactly (both store a
	// plain string), except rich text, which strips its formatting first.
	// Landing a value the target's write path then refuses is not this
	// matrix's job to prevent: the 400 names the element, exactly as it does
	// for a typed-in value.
	if (textSource) {
		return sourceType === 'rich-text' ? coercible('stripFormatting') : exact();
	}
	return unmappable(`No conversion from ${sourceType} to ${targetType}.`);
}

/**
 * The color rows of the matrix. A color is a hex string: it lands in text
 * exactly, and text lands in it through textToColor, which keeps only a value
 * that spells a color. A link, a phone number, a number or a date is never a
 * color, and a color is none of them. MIRROR of the API's classifyColor —
 * change them together.
 */
function classifyColor(sourceType, targetType) {
	if (sourceType === COLOR && targetType === COLOR) return exact();
	if (sourceType === COLOR) {
		if (TEXT_TARGETS.has(targetType)) return exact();
		return unmappable(`No conversion from a color to ${targetType}.`);
	}
	if (COLOR_TEXT_SOURCES.has(sourceType)) return coercible('textToColor');
	return unmappable(`No conversion from ${sourceType} to a color.`);
}

/**
 * The user rows of the matrix (choice targets and measurements were settled
 * above). An account id reads as a display name in plain text and nowhere
 * else; nothing but another user element fills a user element. MIRROR of
 * the API's classifyUser — change them together.
 */
function classifyUser(sourceType, targetType) {
	if (sourceType === USER && targetType === USER) return exact();
	if (sourceType === USER) {
		if (USER_TEXT_TARGETS.has(targetType)) return coercible('userToText');
		return unmappable(`No conversion from a user to ${targetType}.`);
	}
	return unmappable('A user element holds an account chosen from the collaborators; nothing converts into it.');
}

/** The measurement rows of the matrix (choice targets were refused above). */
function classifyMeasurement(sourceType, targetType, sourceOptions, targetOptions) {
	if (sourceType === MEASUREMENT && targetType === MEASUREMENT) {
		const sourceMeasure = getMeasure(sourceOptions && sourceOptions.measure);
		const targetMeasure = getMeasure(targetOptions && targetOptions.measure);
		if (sourceMeasure && targetMeasure && sourceMeasure.id === targetMeasure.id && !isMultiMeasure(sourceOptions || {}) && !isMultiMeasure(targetOptions || {})) return exact();
		// Several measures (KPR-1085, the API's rule): the target must allow
		// every measure the source may hold.
		const source = allowedMeasures(sourceOptions || {});
		const target = allowedMeasures(targetOptions || {});
		// An own unit (KPR-1109) counts as allowed only under the same id NAMED
		// alike: a rename keeps an id, so the target's may be another unit.
		const has = m => target.includes(m) && (!isOwnUnitId(m) || sameOwnUnit(ownUnitOf(m, sourceOptions || {}), ownUnitOf(m, targetOptions || {})));
		if (source.length && target.length && source.every(has)) return exact();
		// An own unit is named by its id's words.
		if (source.length > 1 || target.length > 1) return unmappable(`A measurement drives another only when the target allows every measure the source does (${source.map(m => m.replace(/^own:/, '')).join(', ') || 'none'}).`);
		return unmappable('Measurements only drive measurements of the same measure (mass to mass, length to length).');
	}
	if (sourceType === MEASUREMENT) {
		// A duration is a LENGTH of time; a `time` element holds a wall
		// clock. `elapsed()` answers "eight hours", not "eight o'clock".
		if (targetType === 'time') return unmappable('A duration is a length of time, not a clock reading — use dateAdd({{t}}, n, "min") to move a clock.');
		// Text targets get the formatted value ("8 lb 7 oz"); a url or a
		// choice is never a rendering of a quantity.
		if (targetType === 'text-small' || targetType === 'text-large' || targetType === 'rich-text') return coercible('toText');
		if (targetType === 'number' || targetType === 'decimal' || targetType === 'integer') {
			if (isMultiMeasure(sourceOptions || {})) return unmappable('A measurement that allows several measures has no one unit to be a number in.');
			return coercible('measurementToNumber');
		}
		return unmappable(`No conversion from measurement to ${targetType}.`);
	}
	if (NUMERIC.has(sourceType)) return coercible('numberToMeasurement');
	return unmappable(`No conversion from ${sourceType} to measurement.`);
}

/**
 * The currency rows of the matrix (choice targets and measurement pairs were
 * refused above). There is no conversion between currencies (v1), so one
 * currency element drives another only when the target ALLOWS every
 * currency the source may hold: a copied CAD 40 must be storable, not
 * refused by the target's allowlist at write time. MIRROR of the API's
 * classifyCurrency — change them together.
 */
function classifyCurrency(sourceType, targetType, sourceOptions, targetOptions) {
	if (sourceType === CURRENCY && targetType === CURRENCY) {
		const source = allowedCodes(sourceOptions);
		const target = allowedCodes(targetOptions);
		if (source.length && target.length && source.every(code => target.includes(code))) return exact();
		return unmappable('Currency elements only drive currency elements that allow every currency the source allows.');
	}
	if (sourceType === CURRENCY) {
		// Text targets get the formatted value ("$1,234.50"); a url or a choice
		// is never a rendering of an amount.
		if (TEXT_TARGETS.has(targetType)) return coercible('toText');
		if (targetType === 'number' || targetType === 'decimal' || targetType === 'integer') return coercible('currencyToNumber');
		return unmappable(`No conversion from currency to ${targetType}.`);
	}
	if (NUMERIC.has(sourceType)) return coercible('numberToCurrency');
	return unmappable(`No conversion from ${sourceType} to currency.`);
}

/** Full detail for one transform name (lossy flag + user-facing warning). */
export function transformInfo(name) {
	const t = TRANSFORMS[name];
	return t ? { name, lossy: t.lossy, warning: t.warning || null } : null;
}
